<template>
  <NotFound v-if="paymentNotFound" />
  <div v-else class="checkout-page-wrapper fill-height">
    <v-container class="py-10">
      <div class="d-flex flex-column map-wrapper">
        <v-dialog
          v-model="dialog"
          :fullscreen="isMobile"
          persistent
          max-width="450px"
        >
          <v-card
            class="d-flex pa-7 justify-center align-center"
            style="position: relative"
            height="450"
          >
            <v-col v-if="status === 'processing'" cols="9">
              <DvrLoader />
            </v-col>
            <div
              v-else
              class="d-flex flex-column justify-center align-center flex"
            >
              <img
                src="@/assets/images/payment-success.png"
                width="200"
                alt=""
              />
              <div class="text-h6 my-3">
                {{
                  btPublicApiKey
                    ? $t('PaymentInfoReceived')
                    : $t('PaymentSuccessful')
                }}
              </div>
              <div class="text-caption my-3 px-7 px-sm-3">
                {{
                  btPublicApiKey
                    ? $t('BookingReservedMessage')
                    : $t('BookingConfirmationMessage')
                }}
              </div>
              <v-btn
                class="mt-3 px-10"
                color="primary"
                @click="goToReservation"
              >
                {{ $t('OK') }}
              </v-btn>
            </div>
          </v-card>
        </v-dialog>
        <div v-if="isDeposit" class="text-h4 font-weight-bold mb-2">
          {{ $t('BookingDeposit') }}
        </div>
        <div v-else class="text-h4 font-weight-bold mb-2">
          {{ $t('Checkout') }}
        </div>

        <div
          v-if="isDeposit"
          class="text-body-1 grey--text text--darken-1 mb-8"
          style="max-width: 800px"
        >
          Hello <b>{{ reservation.guest.full_name }}</b
          >, Thank you for choosing {{ listingName }} for your vacation rental
          stay! <br />
          We are notifying you that a security deposit authorization hold of
          <b>{{
            toCurrency(payment.amount / 100, {
              currency: payment.currency?.toUpperCase() || reservation.currency,
            })
          }}</b>
          will be placed on the credit card used for booking on
          {{
            formatDate(
              reservation.check_in,
              'YY/MM/DD',
              reservation.listing.timezone
            )
          }}
          -
          {{
            formatDate(
              reservation.check_out,
              'YY/MM/DD',
              reservation.listing.timezone
            )
          }}. This hold will be released up to 2 days after checkout pending no
          damages were incurred at property.
        </div>
        <div
          v-else
          class="text-body-1 grey--text text--darken-1 mb-10"
          style="max-width: 800px; line-height: 1.6"
        >
          <span
            v-html="
              $t('WelcomeMessage', {
                guestName: reservation.guest.full_name,
                listingName: listingName,
              })
            "
          >
          </span>
          <span
            v-if="!$vuetify.breakpoint.xs"
            v-html="$t('PostBookingMessage')"
            class="ms-1"
          >
          </span>
        </div>
        <v-row>
          <v-col cols="12" md="7" lg="7">
            <!-- Left Column: Payment Form -->
            <div class="mr-md-6">
              <ReservationCheckoutSummary
                v-if="isMobileOrIPad"
                :reservation="reservation"
                :payment="payment"
                class="mb-6"
              />

              <!-- Payment Components -->
              <tokenizer
                v-if="btPublicApiKey"
                :payment-intent-id="payment.payment_intent_id"
                button-text="Confirm & pay"
                :reservation="reservation"
                :bt-public-api-key="btPublicApiKey"
                :submit-disabled="!!termsOfUse && !termsAccepted"
                @payment-success="paymentSuccess"
                @payment-processing="paymentProcessing"
                @payment-failed="paymentFailed"
              />
              <spreedly
                v-else-if="spreedlyEnvironmentKey"
                :environment-key="spreedlyEnvironmentKey"
                :payment-intent-id="payment.payment_intent_id"
                button-text="Confirm & pay"
                @payment-success="paymentSuccess"
                @payment-processing="paymentProcessing"
                @payment-failed="paymentFailed"
                :reservation="reservation"
              />
              <bridger-blox
                v-else-if="bridgerpayCredentials"
                :session-token="bridgerpayCredentials.cashier_token"
                :intent-id="payment.payment_intent_id"
                :initial-name="reservation.guest.full_name"
                :initial-email="reservation.guest.primary_email"
                :initial-phone="reservation.guest.primary_phone"
                :require-address-fields="requireAddressFields"
                :show-apple-pay="showApplePay"
                :submit-disabled="!!termsOfUse && !termsAccepted"
                @payment-processing="paymentProcessing"
                @payment-success="paymentSuccess"
                @payment-failed="paymentFailed"
              />
              <StripeElement
                v-else-if="publicKey"
                :public-key="publicKey"
                :client-secret="clientSecret"
                :reservation="reservation"
                button-text="Confirm & pay"
                @payment-success="paymentSuccess"
                @payment-processing="paymentProcessing"
                @payment-failed="paymentFailed"
              />

              <div
                v-if="termsOfUse"
                class="d-flex mt-6 pa-4 rounded grey lighten-4"
              >
                <v-checkbox
                  v-model="termsAccepted"
                  class="mt-0 pt-0"
                  color="primary"
                  hide-details
                ></v-checkbox>
                <div
                  class="custom-terms-of-use text-body-2 black--text ml-2"
                  v-html="termsOfUse"
                ></div>
              </div>
            </div>
          </v-col>

          <v-col v-if="!isMobileOrIPad" cols="12" md="5" lg="5">
            <!-- Right Column: Summary -->
            <div class="sticky-top" style="top: 24px">
              <ReservationCheckoutSummary
                :reservation="reservation"
                :payment="payment"
              />
            </div>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </div>
</template>

<script>
import StripeElement from 'components/stripe/StripeCustomElement'
import deviceMixin from 'components/mixins/deviceMixin'
import commonFunctions from 'components/mixins/common-functions'
import DvrLoader from '@/assets/icons/dvr-loader'
import axios from 'axios'
import Spreedly from 'components/spreedly'
import BridgerBlox from 'components/bridgerpay/bridger-blox'
import Tokenizer from '@/components/tokenizer/tokenizer'
import ReservationCheckoutSummary from '@/components/reservations/reservation-checkout-summary'
import NotFound from '@/components/common/not-found'
import { HOTJAR_EVENTS } from '@/lib/analytics/hotjar'
import { mapActions } from 'vuex'
import get from 'lodash/fp/get'

export default {
  name: 'StandaloneCheckout',
  components: {
    NotFound,
    ReservationCheckoutSummary,
    DvrLoader,
    StripeElement,
    Spreedly,
    BridgerBlox,
    Tokenizer,
  },
  mixins: [deviceMixin, commonFunctions],
  data() {
    return {
      dialog: false,
      status: null,
      mainReservationCode: null,
      clientSecret: '',
      publicKey: '',
      spreedlyEnvironmentKey: '',
      payment: {},
      reservation: { listing: {}, guest: {}, invoice_items: [] },
      paymentNotFound: false,
      bridgerpayCredentials: null,
      btPublicApiKey: '',
      requireAddressFields: false,
      showApplePay: false,
      termsAccepted: false,
      termsOfUse: '',
    }
  },
  computed: {
    paymentIntentId() {
      return this.$route.params.intent_id
    },
    isDeposit() {
      return this.payment.capture_method === 'manual'
    },
    listingName() {
      return get('listing.resort_name', this.reservation)
    },
  },
  async mounted() {
    console.log('paymentIntentId', this.paymentIntentId)
    try {
      const {
        data: {
          payment,
          reservation,
          stripe_public_key,
          spreedly_environment_key,
          bridgerpay_credentials,
          require_address_fields,
          show_apple_pay,
          bt_public_api_key,
          terms_of_use,
        },
      } = await axios.get(`/api/checkout-info/${this.paymentIntentId}`)
      this.reservation = reservation

      if (
        payment.status === 'succeeded' ||
        payment.status === 'requires_capture' ||
        (this.reservation.balance_due === 0 &&
          payment.metadata.type === 'accommodation')
      ) {
        this.status = 'success'
        this.dialog = true
        return
      }
      this.clientSecret = payment.client_secret
      this.publicKey = stripe_public_key
      this.spreedlyEnvironmentKey = spreedly_environment_key
      this.bridgerpayCredentials = bridgerpay_credentials
      this.btPublicApiKey = bt_public_api_key
      this.requireAddressFields = require_address_fields
      this.showApplePay = show_apple_pay
      this.termsOfUse = terms_of_use
      this.payment = payment
    } catch (err) {
      this.$store.commit('updateLoading', false)
      if (err.response.status === 404) {
        this.paymentNotFound = true
      }
    }
  },
  methods: {
    ...mapActions(['logAction']),
    paymentSuccess({ user }) {
      this.logAction({
        eventType: HOTJAR_EVENTS.PAYMENT_SUCCESSFUL,
        storeItemId: this.checkoutItem && this.checkoutItem.store_item_id,
      })
      this.dialog = true
      setTimeout(() => {
        this.status = 'success'
        this.user = user
      }, 3000)
    },
    paymentProcessing() {
      this.logAction({
        eventType: HOTJAR_EVENTS.PAYMENT_ATTEMPT,
        storeItemId: this.checkoutItem && this.checkoutItem.store_item_id,
      })
      this.status = 'processing'
      this.dialog = true
    },
    paymentFailed() {
      this.dialog = false
      this.status = 'failed'
    },
    goToReservation() {
      window.location.href = `/tourist/trips/${this.reservation.confirmation_code}`
    },
  },
}
</script>

<style scoped>
.checkout-page-wrapper {
  background-color: #f8f9fa;
  min-height: 100vh;
}

:deep(.custom-terms-of-use a) {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
}

:deep(.custom-terms-of-use a:hover) {
  text-decoration: underline;
}

:deep(.custom-terms-of-use p) {
  margin-bottom: 0px;
}
</style>
